import * as ReactDOM from 'react-dom/client'
import { useState, useEffect, useRef, useCallback } from 'react'
import { Player } from '@remotion/player'
import type { PlayerRef } from '@remotion/player'
import { interpolate } from 'remotion'
import ErrorBoundary from './ErrorBoundary'
import ErrorPage from './error-page'
import './preview.css'

// --- Icons ---

function PlayIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
			<path
				fill="currentColor"
				d="M4.5 2.36c0-.9.98-1.45 1.73-.97l9.14 5.64c.7.43.7 1.51 0 1.94L6.23 14.61c-.75.48-1.73-.07-1.73-.97V2.36z"
			/>
		</svg>
	)
}

function PauseIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
			<rect x="3.5" y="2.5" width="4" height="13" rx="1" fill="currentColor" />
			<rect x="10.5" y="2.5" width="4" height="13" rx="1" fill="currentColor" />
		</svg>
	)
}

function VolumeHighIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
			<path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
			<path
				d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
			/>
		</svg>
	)
}

function VolumeLowIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
			<path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
			<path
				d="M15.54 8.46a5 5 0 010 7.07"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
			/>
		</svg>
	)
}

function VolumeMutedIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
			<path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
			<path
				d="M17 9.5l5 5m0-5l-5 5"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
			/>
		</svg>
	)
}

function FullscreenEnterIcon() {
	return (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
			<path
				d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

function FullscreenExitIcon() {
	return (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
			<path
				d="M6 2v4H2M10 6h4V2M14 10h-4v4M6 14v-4H2"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

// --- Control Components ---

function PlayPauseButton({
	playerRef,
}: {
	playerRef: React.RefObject<PlayerRef | null>
}) {
	const [playing, setPlaying] = useState(false)

	useEffect(() => {
		const { current } = playerRef
		setPlaying(current?.isPlaying() ?? false)
		if (!current) return

		const onPlay = () => setPlaying(true)
		const onPause = () => setPlaying(false)
		current.addEventListener('play', onPlay)
		current.addEventListener('pause', onPause)
		return () => {
			current.removeEventListener('play', onPlay)
			current.removeEventListener('pause', onPause)
		}
	}, [playerRef])

	return (
		<button
			type="button"
			onClick={() => playerRef.current?.toggle()}
			className="vc-btn vc-play-pause"
			title={playing ? 'Pause' : 'Play'}
		>
			{playing ? <PauseIcon /> : <PlayIcon />}
		</button>
	)
}

function formatTime(frame: number, fps: number): string {
	const totalSeconds = Math.floor(frame / fps)
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function TimeDisplay({
	durationInFrames,
	fps,
	playerRef,
}: {
	durationInFrames: number
	fps: number
	playerRef: React.RefObject<PlayerRef | null>
}) {
	const [time, setTime] = useState(0)

	useEffect(() => {
		const { current } = playerRef
		if (!current) return
		const onFrameUpdate = () => setTime(current.getCurrentFrame())
		current.addEventListener('frameupdate', onFrameUpdate)
		return () => current.removeEventListener('frameupdate', onFrameUpdate)
	}, [playerRef])

	return (
		<span className="vc-time">
			{formatTime(time, fps)}
			<span className="vc-time-sep">/</span>
			{formatTime(durationInFrames, fps)}
		</span>
	)
}

function SeekBar({
	durationInFrames,
	playerRef,
}: {
	durationInFrames: number
	playerRef: React.RefObject<PlayerRef | null>
}) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [playing, setPlaying] = useState(false)
	const [frame, setFrame] = useState(0)
	const [hovered, setHovered] = useState(false)

	useEffect(() => {
		const { current } = playerRef
		if (!current) return
		const onFrame = () => setFrame(current.getCurrentFrame())
		const onPlay = () => setPlaying(true)
		const onPause = () => setPlaying(false)
		current.addEventListener('frameupdate', onFrame)
		current.addEventListener('play', onPlay)
		current.addEventListener('pause', onPause)
		return () => {
			current.removeEventListener('frameupdate', onFrame)
			current.removeEventListener('play', onPlay)
			current.removeEventListener('pause', onPause)
		}
	}, [playerRef])

	const [dragging, setDragging] = useState<
		{ dragging: false } | { dragging: true; wasPlaying: boolean }
	>({ dragging: false })

	const getFrame = useCallback(
		(clientX: number) => {
			const rect = containerRef.current?.getBoundingClientRect()
			if (!rect || rect.width === 0) return 0
			return Math.round(
				interpolate(
					clientX - rect.left,
					[0, rect.width],
					[0, Math.max(durationInFrames - 1, 0)],
					{ extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
				)
			)
		},
		[durationInFrames]
	)

	const onPointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (e.button !== 0 || !playerRef.current) return
			playerRef.current.pause()
			playerRef.current.seekTo(getFrame(e.clientX))
			setDragging({ dragging: true, wasPlaying: playing })
		},
		[getFrame, playerRef, playing]
	)

	const onPointerMove = useCallback(
		(e: PointerEvent) => {
			if (!dragging.dragging || !playerRef.current) return
			playerRef.current.seekTo(getFrame(e.clientX))
		},
		[dragging.dragging, getFrame, playerRef]
	)

	const onPointerUp = useCallback(() => {
		if (!dragging.dragging || !playerRef.current) {
			setDragging({ dragging: false })
			return
		}
		if (dragging.wasPlaying) playerRef.current.play()
		setDragging({ dragging: false })
	}, [dragging, playerRef])

	useEffect(() => {
		if (!dragging.dragging) return
		document.addEventListener('pointermove', onPointerMove)
		document.addEventListener('pointerup', onPointerUp)
		return () => {
			document.removeEventListener('pointermove', onPointerMove)
			document.removeEventListener('pointerup', onPointerUp)
		}
	}, [dragging.dragging, onPointerMove, onPointerUp])

	const progress = frame / Math.max(1, durationInFrames - 1)
	const active = hovered || dragging.dragging

	return (
		<div
			ref={containerRef}
			onPointerDown={onPointerDown}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className={`vc-seekbar${active ? ' active' : ''}`}
			style={{ '--progress': progress } as React.CSSProperties}
		>
			<div className="vc-seekbar-track">
				<div className="vc-seekbar-fill" />
			</div>
			<div className="vc-seekbar-knob" />
		</div>
	)
}

function VolumeControl({
	playerRef,
}: {
	playerRef: React.RefObject<PlayerRef | null>
}) {
	const [volume, setVolume] = useState(1)
	const [muted, setMuted] = useState(false)

	useEffect(() => {
		const { current } = playerRef
		if (!current) return
		setVolume(current.getVolume())
		setMuted(current.isMuted())
		const onVolume = () => setVolume(current.getVolume())
		const onMute = () => setMuted(current.isMuted())
		current.addEventListener('volumechange', onVolume)
		current.addEventListener('mutechange', onMute)
		return () => {
			current.removeEventListener('volumechange', onVolume)
			current.removeEventListener('mutechange', onMute)
		}
	}, [playerRef])

	const effectiveVolume = muted ? 0 : volume

	return (
		<div className="vc-volume-group">
			<button
				type="button"
				className="vc-btn vc-mute-btn"
				onClick={() => {
					if (!playerRef.current) return
					playerRef.current.isMuted()
						? playerRef.current.unmute()
						: playerRef.current.mute()
				}}
				title={muted ? 'Unmute' : 'Mute'}
			>
				{effectiveVolume === 0 ? (
					<VolumeMutedIcon />
				) : effectiveVolume < 0.5 ? (
					<VolumeLowIcon />
				) : (
					<VolumeHighIcon />
				)}
			</button>
			<input
				type="range"
				min={0}
				max={1}
				step={0.01}
				value={effectiveVolume}
				className="vc-volume-slider"
				onChange={e => {
					if (!playerRef.current) return
					const v = Number(e.target.value)
					if (v > 0 && playerRef.current.isMuted()) playerRef.current.unmute()
					playerRef.current.setVolume(v)
				}}
			/>
		</div>
	)
}

function FullscreenButton({
	playerRef,
}: {
	playerRef: React.RefObject<PlayerRef | null>
}) {
	const [isFs, setIsFs] = useState(false)
	const [supported, setSupported] = useState(false)

	useEffect(() => {
		setSupported(document.fullscreenEnabled ?? false)
	}, [])

	useEffect(() => {
		const { current } = playerRef
		if (!current) return
		const onChange = () => setIsFs(document.fullscreenElement !== null)
		current.addEventListener('fullscreenchange', onChange)
		return () => current.removeEventListener('fullscreenchange', onChange)
	}, [playerRef])

	if (!supported) return null

	return (
		<button
			type="button"
			onClick={() => {
				if (!playerRef.current) return
				isFs
					? playerRef.current.exitFullscreen()
					: playerRef.current.requestFullscreen()
			}}
			className="vc-btn vc-fullscreen-btn"
			title={isFs ? 'Exit fullscreen' : 'Fullscreen'}
		>
			{isFs ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
		</button>
	)
}

// Glob import to ensure Vite bundles all campaign type modules
const campaignTypeModules = import.meta.glob('./campaigntypes/*/index.tsx', {
	eager: false,
})

const paths = window.location.pathname.split('/').slice(1)
const [campaignTypeReference, platform, width, height] = paths

function Preview() {
	const [props, setProps] = useState({
		platform,
		adWidth: Number(width),
		adHeight: Number(height),
		campaignTypeName: campaignTypeReference,
		customFields: [] as any[],
	})
	// Track if we've received at least one postMessage payload so we can optionally defer rendering until real data arrives.
	const [hasReceivedMessage, setHasReceivedMessage] = useState(false)
	const [durationInFrames, setDurationInFrames] = useState(210)
	const [fps, setFps] = useState(30)
	const currentComponent = useRef<any>(null)
	const [componentLoaded, setComponentLoaded] = useState(false)
	const [error, setError] = useState<Error | null>(null)
	const pendingMessage = useRef<any>(null)
	const playerRef = useRef<PlayerRef>(null)
	const [isFullscreen, setIsFullscreen] = useState(false)

	useEffect(() => {
		const { current } = playerRef
		if (!current) return
		const onFsChange = () => setIsFullscreen(document.fullscreenElement !== null)
		current.addEventListener('fullscreenchange', onFsChange)
		return () => {
			current.removeEventListener('fullscreenchange', onFsChange)
		}
	}, [hasReceivedMessage])

	const processMessage = (messageProps: any) => {
		if (currentComponent?.current?.meta) {
			currentComponent.current
				.meta({ props: messageProps })
				.then((metaResult: any) => {
					const metaProps = { ...messageProps, ...metaResult }
					setProps(metaProps)
					if (metaResult.durationInFrames) {
						setDurationInFrames(metaResult.durationInFrames)
					}
					if (metaResult.fps) {
						setFps(metaResult.fps)
					}
					setHasReceivedMessage(true)
				})
				.catch((err: unknown) => {
					console.error('Error in meta function:', err)
				})
		} else {
			if (
				currentComponent?.current?.default &&
				!currentComponent?.current.meta
			) {
				console.info(
					'No meta function found for:',
					campaignTypeReference,
					currentComponent?.current?.default
				)
			}
			setProps(messageProps)
			setHasReceivedMessage(true)
		}
	}

	if (paths.length !== 4) {
		console.error('Invalid path length:', paths.length, paths)
		throw new Error(
			'Invalid path, please provide: campaignTypeReference, platform, width and height'
		)
	}

	useEffect(() => {
		// Construct the module path
		const modulePath = `./campaigntypes/${campaignTypeReference}/index.tsx`
		if (campaignTypeModules[modulePath]) {
			campaignTypeModules[modulePath]()
				.then((module: any) => {
					const component =
						module.default || module[campaignTypeReference] || module.index
					if (!component) {
						throw new Error(
							`No valid component found in module for ${campaignTypeReference}`
						)
					}
					currentComponent.current = { default: component, meta: module.meta }
					if (pendingMessage.current) {
						processMessage(pendingMessage.current)
						pendingMessage.current = null
					}
					setComponentLoaded(true)
				})
				.catch((error: unknown) => {
					console.error('Error loading module:', (error as any)?.message)
					setError(error as Error)
				})
		} else {
			const err = new Error(`Campaign type ${campaignTypeReference} not found`)
			console.error(err.message)
			setError(err)
		}
	}, [campaignTypeReference])

	if (error) {
		console.error('Rendering error:', error.message)
		throw error
	}

	function receiveMessage(event: { data: any }) {
		if (
			!event.data ||
			typeof event.data !== 'object' ||
			!('type' in event.data) ||
			event.data.type !== 'iframead'
		) {
			return
		}

		const incomingCustomFields = Array.isArray(event.data.customFields)
			? event.data.customFields
			: []

		const messageProps = {
			platform,
			adWidth: Number(width),
			adHeight: Number(height),
			campaignTypeName: campaignTypeReference,
			...event.data,
			customFields: incomingCustomFields,
		}

		if (currentComponent.current) {
			processMessage(messageProps)
		} else {
			pendingMessage.current = messageProps
		}
	}

	useEffect(() => {
		window.addEventListener('message', receiveMessage, false)
		return () => {
			window.removeEventListener('message', receiveMessage)
		}
	}, [])
	// Only render when component is loaded and we've either received a message OR we allow initial empty array render.
	const componentReady =
		!!currentComponent.current && !!currentComponent.current.default
	const timingReady = hasReceivedMessage // enforce waiting for data to avoid race conditions
	if (!componentReady || !timingReady) return null

	const aspectRatio = Number(width) / Number(height)

	return (
		<div
			className="vc-wrapper"
			style={{ '--player-aspect-ratio': aspectRatio } as React.CSSProperties}
		>
			<Player
				ref={playerRef}
				loop
				clickToPlay
				controls={isFullscreen}
				component={currentComponent.current.default}
				fps={fps}
				durationInFrames={durationInFrames}
				compositionWidth={Number(width)}
				compositionHeight={Number(height)}
				className="remotion-video-player"
				inputProps={props}
				allowFullscreen
				acknowledgeRemotionLicense
			/>
			<div
				className="vc-controls"
				style={isFullscreen ? { display: 'none' } : undefined}
			>
				<SeekBar
					durationInFrames={durationInFrames}
					playerRef={playerRef}
				/>
				<div className="vc-buttons-row">
					<PlayPauseButton playerRef={playerRef} />
					<TimeDisplay
						durationInFrames={durationInFrames}
						fps={fps}
						playerRef={playerRef}
					/>
					<div className="vc-spacer" />
					<VolumeControl playerRef={playerRef} />
					<FullscreenButton playerRef={playerRef} />
				</div>
			</div>
		</div>
	)
}

ReactDOM.createRoot(document.getElementById('root')!).render(
	<ErrorBoundary fallback={<ErrorPage error="Finner ikke annonse." />}>
		<Preview />
	</ErrorBoundary>
)
